import React, { useContext, useEffect, useState } from 'react';
import { requestJson } from '../services/api';
import { ToastContext } from '../App';
import { ClipboardCopyIcon, HistoryIcon, KeyIcon, Loader2Icon, TrashIcon } from '../constants';

interface SyncToken {
  id: number;
  label: string;
  prefix: string;
  createdAt: string;
  lastUsedAt: string | null;
}

/** Zugangsschlüssel für Skripte wie den Skill-Sync: anlegen (Klartext nur einmal sichtbar), auflisten, widerrufen. */
const SyncTokens: React.FC = () => {
  const { showToast } = useContext(ToastContext);
  const [tokens, setTokens] = useState<SyncToken[]>([]);
  const [label, setLabel] = useState('');
  const [newToken, setNewToken] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const load = async () => {
    try {
      const res = await requestJson<{ tokens: SyncToken[] }>('/api/tokens');
      setTokens(res.tokens);
    } catch {
      showToast('Zugangsschlüssel konnten nicht geladen werden.', 'error');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsBusy(true);
    try {
      const res = await requestJson<{ token: string }>('/api/tokens', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: label.trim() || 'Skill-Sync' }),
      });
      setNewToken(res.token);
      setLabel('');
      await load();
    } catch (err) {
      showToast((err as Error).message || 'Schlüssel konnte nicht angelegt werden.', 'error');
    } finally {
      setIsBusy(false);
    }
  };

  const handleRevoke = async (id: number) => {
    try {
      await requestJson(`/api/tokens/${id}`, { method: 'DELETE' });
      setTokens(current => current.filter(t => t.id !== id));
      showToast('Zugangsschlüssel widerrufen.');
    } catch {
      showToast('Widerrufen fehlgeschlagen.', 'error');
    }
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => showToast('Schlüssel kopiert!'), () => showToast('Fehler beim Kopieren', 'error'));
  };

  return (
    <div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Mit einem Zugangsschlüssel kann ein Skript auf deinem Rechner (z. B. <code>scripts/skill-sync.mjs</code>) deine Skills in die Bibliothek einsortieren, ohne dein Passwort zu kennen. Der Schlüssel darf nur Skills syncen, nicht dein Konto verwalten.
      </p>

      {newToken && (
        <div className="mb-4 p-4 rounded-lg bg-green-500/10 space-y-2">
          <p className="text-sm font-semibold text-green-700 dark:text-green-300">Neuer Schlüssel – er wird nur jetzt angezeigt:</p>
          <div className="flex items-center gap-2 p-2 rounded-md bg-gray-200 dark:bg-gray-800">
            <code className="flex-grow font-mono text-xs break-all">{newToken}</code>
            <button type="button" onClick={() => copy(newToken)} className="p-2 rounded hover:bg-gray-300 dark:hover:bg-gray-600" aria-label="Schlüssel kopieren"><ClipboardCopyIcon /></button>
          </div>
          <div className="flex justify-end">
            <button type="button" onClick={() => setNewToken(null)} className="btn-secondary">Gespeichert, ausblenden</button>
          </div>
        </div>
      )}

      <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1">
          <label htmlFor="syncTokenLabel" className="text-sm font-medium text-gray-600 dark:text-gray-300">Bezeichnung</label>
          <input type="text" id="syncTokenLabel" value={label} onChange={e => setLabel(e.target.value)} className="input-field" placeholder="z. B. Skill-Sync Laptop" maxLength={100} />
        </div>
        <button type="submit" disabled={isBusy} className="btn-primary w-48 flex items-center justify-center gap-2">
          {isBusy ? <Loader2Icon /> : <><KeyIcon /> Schlüssel anlegen</>}
        </button>
      </form>

      {tokens.length > 0 && (
        <ul className="mt-4 space-y-2">
          {tokens.map(t => (
            <li key={t.id} className="flex items-center justify-between rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{t.label} <code className="ml-2 text-xs text-gray-500">{t.prefix}…</code></p>
                <p className="text-[10px] text-gray-500 flex items-center gap-1">
                  <HistoryIcon className="w-3 h-3" />
                  {t.lastUsedAt ? `Zuletzt genutzt ${new Date(t.lastUsedAt).toLocaleString('de-DE')}` : 'Noch nie genutzt'}
                </p>
              </div>
              <button type="button" onClick={() => handleRevoke(t.id)} className="text-gray-400 hover:text-red-500 p-2" title="Schlüssel widerrufen" aria-label="Schlüssel widerrufen">
                <TrashIcon />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SyncTokens;
